/**
 * CRT shader: scanlines + barrel curvature + chromatic fringe.
 *
 * uTexture: source frame (already laid out by drawElementImage into a staging canvas)
 * uResolution: staging canvas size in CSS px
 * uIntensity: 0..1 — controls scanline strength, curvature, fringe magnitude
 * uTime: seconds; used for subtle phosphor flicker
 */
export const crtVertexShader = /* glsl */ `
attribute vec2 a_position;
attribute vec2 a_texCoord;
varying vec2 v_uv;
void main() {
  v_uv = a_texCoord;
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

export const crtFragmentShader = /* glsl */ `
precision mediump float;
uniform sampler2D uTexture;
uniform vec2 uResolution;
uniform float uIntensity;
uniform float uTime;
varying vec2 v_uv;

vec2 barrel(vec2 uv, float k) {
  vec2 c = uv - 0.5;
  float r2 = dot(c, c);
  return uv + c * r2 * k;
}

void main() {
  float curvature = 0.15 * uIntensity;
  vec2 uv = barrel(v_uv, curvature);

  // outside the curved viewport: black
  if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) {
    gl_FragColor = vec4(0.0, 0.0, 0.0, 0.0);
    return;
  }

  // chromatic-like fringe scaled by intensity
  float fringe = 0.0025 * uIntensity;
  float r = texture2D(uTexture, uv + vec2(fringe, 0.0)).r;
  float g = texture2D(uTexture, uv).g;
  float b = texture2D(uTexture, uv - vec2(fringe, 0.0)).b;
  float a = texture2D(uTexture, uv).a;

  vec3 color = vec3(r, g, b);

  // scanlines: dark bands every ~2px in pixel space
  float lines = sin(uv.y * uResolution.y * 3.14159) * 0.5 + 0.5;
  float scan = mix(1.0, lines, 0.35 * uIntensity);
  color *= scan;

  // subtle phosphor flicker
  color *= 1.0 - 0.02 * uIntensity * sin(uTime * 12.0);

  // slight vignette
  float vig = smoothstep(0.85, 0.4, length(v_uv - 0.5));
  color *= mix(1.0, vig, 0.4 * uIntensity);

  gl_FragColor = vec4(color, a);
}
`;
